import type { InboxSnapshot, LeadDestination } from './cleverTap'

type WebInboxMessage = {
  id: string; date: number; wzrk_id: string; wzrk_pivot?: string; wzrk_ttl?: number; viewed?: number
  kv?: { recipientEmployeeId?: string; leadId?: string }
  msg?: { title?: string; description?: string; buttons?: { url?: string }[] }[]
}
type WebSdk = {
  init: (accountId: string, region: string) => void
  privacy: { push: (settings: { useIP: boolean }) => void }
  profile: { push: (profile: object) => void }
  onUserLogin: { push: (profile: object) => void }
  getAllInboxMessages: () => Record<string, WebInboxMessage | null>
  getInboxMessageForId: (id: string) => WebInboxMessage | undefined
  renderNotificationClicked: (event: { msgId: string; pivotId?: string }) => void
  markReadInboxMessage: (id: string) => void
}
type WebNotificationRequest = { employeeId?: string; name?: string; email?: string; messageId?: string }

let sdkPromise: Promise<WebSdk> | undefined
let employeeId: string | null = null
let identifiedEmployee: string | null = null

export const webInboxConfigured = () => !!process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID?.trim()

const getSdk = () => {
  if (!webInboxConfigured()) throw new Error('Notifications are not configured for this website.')
  if (!sdkPromise) {
    sdkPromise = import('clevertap-web-sdk').then((module) => {
      const sdk: WebSdk = module.default
      sdk.privacy.push({ useIP: false })
      sdk.init(process.env.REACT_APP_CLEVERTAP_ACCOUNT_ID.trim(), process.env.REACT_APP_CLEVERTAP_REGION || 'in1')
      return sdk
    }).catch((error) => { sdkPromise = undefined; throw error })
  }
  return sdkPromise
}

const leadFromUrl = (url: unknown): LeadDestination | null => {
  if (typeof url !== 'string') return null
  try {
    const link = new URL(url, window.location.origin)
    const path = link.pathname.match(/^\/notification\/lead\/([^/]+)$/)
    const recipients = link.searchParams.getAll('recipientEmployeeId')
    if (link.origin !== window.location.origin || !path || recipients.length !== 1 || !recipients[0].trim()) return null
    const leadId = decodeURIComponent(path[1])
    return leadId.trim() ? { leadId, recipientEmployeeId: recipients[0] } : null
  } catch { return null }
}

export const webInboxSnapshot = (raw: Record<string, WebInboxMessage | null> | null, identity: string | null): InboxSnapshot => {
  const messages = identity && raw && typeof raw === 'object' ? Object.values(raw).flatMap((message) => {
    if (!message || typeof message.id !== 'string' || !Number.isFinite(message.date)) return []
    const content = message.msg?.[0]
    const link = Array.isArray(content?.buttons)
      ? content.buttons.map((button) => leadFromUrl(button?.url)).find(Boolean) : null
    const recipient = message.kv?.recipientEmployeeId || link?.recipientEmployeeId
    if (recipient !== identity) return []
    const leadId = message.kv?.leadId || link?.leadId || ''
    const expiresAt = Number.isFinite(message.wzrk_ttl) ? message.wzrk_ttl * 1000 : 0
    if (expiresAt > 0 && expiresAt <= Date.now()) return []
    return [{ id: message.id, leadId: typeof leadId === 'string' ? leadId : '', recipientEmployeeId: identity,
      title: typeof content?.title === 'string' ? content.title : '',
      body: typeof content?.description === 'string' ? content.description : '',
      createdAt: message.date, expiresAt, isRead: message.viewed === 1 }]
  }).sort((a, b) => b.createdAt - a.createdAt) : []
  return { configured: webInboxConfigured(), ready: identity !== null, employeeId: identity, messages,
    totalCount: messages.length, unreadCount: messages.filter((message) => !message.isRead).length }
}

export const requestWebNotifications = async <T,>(operation: string, data: WebNotificationRequest = {}): Promise<T> => {
  if (operation === 'logout') {
    employeeId = null
    // Keep the SDK's per-profile history for the next login; the app exposes none while logged out.
    return webInboxSnapshot(null, null) as T
  }
  if (operation === 'identify') {
    if (typeof data.employeeId !== 'string' || !data.employeeId.trim()) throw new Error('Employee ID is required')
    employeeId = data.employeeId
  }
  const identity = employeeId
  const sdk = await getSdk()
  if (identity !== employeeId) return webInboxSnapshot(null, employeeId) as T
  if (operation === 'identify') {
    const profile = { Identity: identity, ...(data.name?.trim() && { Name: data.name.trim() }),
      ...(data.email?.trim() && { Email: data.email.trim() }) }
    if (identifiedEmployee === identity) sdk.profile.push({ Site: profile })
    else {
      sdk.onUserLogin.push({ Site: profile })
      identifiedEmployee = identity
    }
  }
  const snapshot = webInboxSnapshot(sdk.getAllInboxMessages(), identity)
  if (operation === 'openMessage') {
    const message = snapshot.messages.find((item) => item.id === data.messageId)
    if (!message) throw new Error('Notification is no longer available for this employee')
    const raw = sdk.getInboxMessageForId(message.id)
    if (!raw) throw new Error('Notification is no longer available')
    sdk.renderNotificationClicked({ msgId: raw.wzrk_id, pivotId: raw.wzrk_pivot })
    sdk.markReadInboxMessage(message.id)
    return (message.leadId ? { leadId: message.leadId, recipientEmployeeId: identity } : null) as T
  }
  if (!['identify', 'getInbox', 'refreshInbox'].includes(operation)) throw new Error('Unknown notification operation')
  return snapshot as T
}
